import type { IsoDate, TaskInstance } from '@hestia/core';

/** Voice rules from the design system live here so no component invents its own
 *  wording. Nothing is "overdue", nothing is counted, and a finished task names
 *  the person who finished it. */

const D = (d: IsoDate) => new Date(`${d}T00:00:00Z`);

export const dayNumber = (d: IsoDate) => D(d).getUTCDate();

export function weekday(d: IsoDate, long = true): string {
  return D(d).toLocaleDateString('en-GB', { weekday: long ? 'long' : 'short', timeZone: 'UTC' });
}

export function fullDate(d: IsoDate): string {
  return D(d).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });
}

/** "Sunday, 6 September 2026" — the form used wherever an exact day matters. */
export function longDate(d: IsoDate): string {
  return D(d).toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

export function monthTitle(d: IsoDate): string {
  return D(d).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

/** "From Friday", "From 12 August" — never "overdue", never a day count. */
export function fromLabel(date: IsoDate, today: IsoDate): string {
  const days = Math.round((D(today).getTime() - D(date).getTime()) / 86_400_000);
  if (days === 1) return 'From yesterday';
  if (days < 7) return `From ${weekday(date)}`;
  return `From ${D(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', timeZone: 'UTC' })}`;
}

export function relativeDay(date: IsoDate, today: IsoDate): string {
  const days = Math.round((D(date).getTime() - D(today).getTime()) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  if (days > 1 && days < 7) return weekday(date);
  return D(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

export function doneLabel(i: TaskInstance, who: string | null): string {
  const at = i.completedAt
    ? new Date(i.completedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : null;
  if (who && at) return `${who} finished this at ${at}`;
  if (who) return `${who} finished this`;
  return 'Finished';
}

export function greeting(now = new Date()): string {
  const h = now.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

/** Sections are time of day, not priority — the app never ranks your tasks. */
export function bucket(i: TaskInstance): 'morning' | 'later' {
  const t = i.task.startTime;
  return t && t < '12:00' ? 'morning' : 'later';
}

/** How long a span has left, said the way a person would.
 *
 *  Never a count of days remaining. "Two days left" is a countdown, and a
 *  countdown is a scoreboard with a deadline attached — the same thing the rest
 *  of this app refuses to be. It says which day it runs to, and stops. */
export function spanLabel(start: IsoDate, end: IsoDate, today: IsoDate): string {
  if (end < today) return `Ran to ${relativeDay(end, today)}`;
  if (start > today) return `${relativeDay(start, today)} to ${relativeDay(end, today)}`;
  if (end === today) return 'Last day';
  return `Until ${relativeDay(end, today)}`;
}

/** When something is, for a list that reaches past this week: "Tomorrow",
 *  "Thursday", "12 Mar" — and "12 Mar 2028" once it is not this year, because
 *  a bare date two years out reads as this March. */
export function whenLabel(date: IsoDate, today: IsoDate): string {
  const rel = relativeDay(date, today);
  return date.slice(0, 4) === today.slice(0, 4) ? rel
    : D(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}
